module.exports={render:function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _vm._m(0)
},staticRenderFns: [function (){var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;
  return _c('div', {
    staticClass: "chapter"
  }, [_c('div', {
    staticClass: "inner_page"
  }, [_c('div', {
    staticClass: "chapter",
    attrs: {
      "id": "ch01"
    }
  }, [_c('h1', {
    staticClass: "cn"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p2"
    }
  }), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p3"
    }
  }), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0006#toc"
    }
  }, [_vm._v("1")])]), _vm._v(" "), _c('h1', {
    staticClass: "ct"
  }, [_vm._v("The Chromatic Moment in Enlightenment Thought")]), _vm._v(" "), _c('h2', {
    staticClass: "ahaft",
    attrs: {
      "id": "ahaft0101"
    }
  }, [_c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0006#toc"
    }
  }, [_vm._v("A Preamble on Portraiture and Language")])]), _vm._v(" "), _c('p', {
    staticClass: "pf"
  }, [_vm._v("Titles, in their terse ideogrammatic language, have a way of enfolding their subjects in enigma. A title does its work when it seduces the reader into the mysteries of the text that it prefigures, the text itself then a kind of unraveling, an exploration of the title that provokes it. And so to the title of this chapter. “Moment,” at its center of gravity, holds pride of place here and in each of the chapters that follow. Leaning on the German "), _c('i', [_vm._v("Moment")]), _vm._v(", it means to convey a sense of impulse, or even motive, something of substance leaching into the temporal instant, an essence of the music caught in this momentary inflection. In its specificity, the definite article further identifies this moment with the singular event that provokes the music to its extremities. To speak of the moment as "), _c('i', [_vm._v("chromatic")]), _vm._v("—if that is its claim to prominence—is to inquire into the nature of its relationship to the diatonic environment within which it is defined. Must we apprehend this relationship as frozen in the hierarchies of a tonal spectrum, or ought we to hear its elements as variables in tension between two extreme positions?")]), _vm._v(" "), _c('p', [_vm._v("To frame the question this way is to engage the music in the dialectical dramaturgy of Enlightenment thought. Without claiming to know what "), _c('i', [_vm._v("is")]), _vm._v(" the Enlightenment—or more to the point, what it "), _c('i', [_vm._v("was")]), _vm._v("—I shall prefer, here and in the chapters that follow, to tease out those aspects of discourse—of music, of literature, of art and its criticism—that to my mind constitute a way of thinking, of holding a world teetering in ironic imbalance, of taking pleasure in the irreconcilable tension between reason and the irrational that is at the core of the Enlightenment mind.")]), _vm._v(" "), _c('p', [_vm._v("For Moses Mendelssohn, it is the element of surprise—"), _c('i', [_vm._v("Überraschung")]), _vm._v(" is his word—that, in some measure, defines Enlightenment sensibility: “Our happiness depends upon enjoyment and enjoyment depends upon the swift sentiment with which each beauty surprises our senses. Unhappy are those "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p4"
    }
  }), _vm._v("whom reason has hardened against the onset of such a surprise.”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn5",
      "id": "fn5r"
    }
  }, [_vm._v("1")]), _vm._v(" By these lights, it is not the timeless contemplation of beauty that is at issue, but the moment at which our sensibilities are caught off guard. Indeed, it is not clear wherein lies this "), _c('i', [_vm._v("Schönheit")]), _vm._v(" that is revealed to Mendelssohn’s interlocutor, how it is constituted, even whether, as a thing unto itself, it exists at all, or whether what Mendelssohn has in mind is the experience of the moment, this "), _c('i', [_vm._v("Überraschung")]), _vm._v(" with which the sensibilities are overcome. These lines are in fact given to the fictive Euphranor, one of the disputants in an exchange of letters titled “Ueber die Empfindungen”; his counterpart, Theocles, will have much to say in defense of reason in the course of the expansive Platonic dialogue in which these matters are examined. And if there is some question whether Mendelssohn’s sensibilities are in sympathy with the one or the other, perhaps it is enough that the eloquence of Euphranor’s words endows them with an appeal that Mendelssohn himself must have found difficult to dispel.")]), _vm._v(" "), _c('p', [_vm._v("From another perspective, Johann Gottfried Herder’s ever-stimulating essay toward the “Origins of Language” sharpens its focus on the moment of recognition, the moment at which language is created, but also on the very notion of thought: “"), _c('i', [_vm._v("Placed in the state of reflection")]), _vm._v(" [Besonnenheit] "), _c('i', [_vm._v("which is inherent in him, with this reflection for the first time given full freedom of action, man created language")]), _vm._v(". For what is reflection ["), _c('i', [_vm._v("Reflexion")]), _vm._v("]? What is language?”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn6",
      "id": "fn6r"
    }
  }, [_vm._v("2")]), _vm._v(" For the Enlightenment mind, if we may take Herder’s impassioned "), _c('i', [_vm._v("scena")]), _vm._v(" as symptomatic, it is the process of discovery that is prized. “He "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p5"
    }
  }), _vm._v("manifests reflection,” continues Herder, “when, confronted with the vast hovering dream of images which pass by his senses, he can collect himself into a moment of awareness”—of "), _c('i', [_vm._v("Anerkennung")]), _vm._v("."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn7",
      "id": "fn7r"
    }
  }, [_vm._v("3")]), _vm._v(" Of this signal moment, Herder isolates a recognition of the single distinguishing mark that would enable its expression as language: “The first act of this "), _c('i', [_vm._v("Anerkenntnis")]), _vm._v(" results in a clear concept.” In a burst of enthusiasm, the act is celebrated: “"), _c('i', [_vm._v("Wohlan")]), _vm._v("! Let us acclaim him with shouts of eureka!”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn8",
      "id": "fn8r"
    }
  }, [_vm._v("4")]), _vm._v(" As with Mendelssohn on beauty, it is the frisson of recognition that Herder wants us to imagine, this fleeting moment in which the world seems to hang in the balance, that is at the core of Enlightenment thought. In like mode, both Lessing and Goethe seek out this moment in coming to an understanding of the sculpture group Laocoon, to which we shall return in a later chapter.")]), _vm._v(" "), _c('p', [_vm._v("In conjuring the iconic images of an Enlightenment sensibility, one that springs to mind is a portrait in profile by Jean-Baptiste Greuze, a chalk drawing now at the Morgan Library in New York (shown as "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#fig0101"
    }
  }, [_vm._v("fig. 1.1")]), _vm._v("). Its subject is Denis Diderot, age forty-seven, without wig, the exposed head a sign of the free-thinking, independent mind. Diderot thought it a more accurate representation than the famous painting by van Loo: “My children, I warn you that this is not me,” he writes of the van Loo. “In the course of a single day I assumed a hundred different expressions, in accordance with the things that affected me. I was serene, sad, pensive, tender, violent, passionate, enthusiastic. I had a large forehead, penetrating eyes, rather large features, a head quite similar in character to that of an ancient orator, an easygoing nature that sometimes approached the fool, the rustic simplicity of ancient times.”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn9",
      "id": "fn9r"
    }
  }, [_vm._v("5")]), _vm._v(" Still better, in Diderot’s view, was a portrait by “a poor devil named Garant, who,” Diderot writes, “managed to trap me, just as a fool sometimes comes "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p7"
    }
  }), _vm._v("up with a witty remark. Whoever sees my portrait by Garant, sees me. ‘Ecco il vero Polichinello.’”"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn10",
      "id": "fn10r"
    }
  }, [_vm._v("6")]), _vm._v(" To assign to himself this burlesque rôle in the "), _c('i', [_vm._v("commedia dell’arte")]), _vm._v(" is of course a badge of honor, a refusal of the arrogance of the aristocracy, even if we read Diderot’s self-deprecation with a whiff of skepticism. It is the sensibility of the man that Diderot wants depicted. More to our point, it is the transient sense of moment, the suggestion of character as always in motion, that Diderot finds lacking in the stiff formality of the van Loo.")]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "fig0101"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p6"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "images/chi-kramer-fig01001.jpg",
      "width": "470"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 1.1.")]), _vm._v(" Jean-Baptiste Greuze: Portrait in chalk of Denis Diderot. New York: Pierpont Morgan Library. By kind permission.")])]), _vm._v(" "), _c('p', [_vm._v("The Greuze profile, with shirt opened casually at the neck, brings to mind a similar one: the curious lithograph of Joseph Haydn by Adolph Kunike (see "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#fig0102"
    }
  }, [_vm._v("fig. 1.2")]), _vm._v("). With the exception of the bust in antique style engraved by David Weiss, this is the only representation of Haydn without wig; here, too, the shirt is loosely opened at the neck, the collar rumpled. Kunike’s lithograph is riddled in contradictory messages, for it shows a man in his forties (Haydn in the 1770s or even around 1780), but imagined from a date no earlier than 1817 and perhaps as late as the 1820s."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn11",
      "id": "fn11r"
    }
  }, [_vm._v("7")]), _vm._v(" Its credentials as an “authentic” view of Haydn are without merit; at best, Kunike was formulating his Haydn from some contemporary image, perhaps an engraving of 1809 made from the pencil drawing (1794) by George Dance, which Haydn himself considered to be the best likeness."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn12",
      "id": "fn12r"
    }
  }, [_vm._v("8")]), _vm._v(" Kunike’s rare glimpse of an unbuttoned, unwigged Haydn in rumpled house coat conjures the composer in his study, the busy detail of formal portraiture, its superficial signs of authenticity, replaced by an image of a different kind, reaching inward toward the unknowable Haydn beneath the surface. Unintentionally, it conjures Greuze’s profile of Diderot."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn13",
      "id": "fn13r"
    }
  }, [_vm._v("9")])]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "fig0102"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p8"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "images/chi-kramer-fig01002.jpg",
      "width": "501"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 1.2.")]), _vm._v(" Adolf Kunike: Portrait of Joseph Haydn, lithograph print (Vienna, ca. 1817). Private collection.")])]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p9"
    }
  }), _vm._v("Then there is Joseph Lange’s provocatively unfinished oil of Mozart (shown as "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#fig0103"
    }
  }, [_vm._v("fig. 1.3")]), _vm._v(")—“am Klavier,” as it is called, though the keyboard remained sealed in Lange’s imagination."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn14",
      "id": "fn14r"
    }
  }, [_vm._v("10")]), _vm._v(" The only authenticated image of "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p10"
    }
  }), _vm._v("Mozart without wig—“natürliches Haar (braun),” in Otto E. Deutsch’s description of it—the painting captures the immediacy of mind, the eyes fixed with an intensity almost exaggerated, as though turned inward, focused on something not visible to us, oblivious of the painter and his audience."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn15",
      "id": "fn15r"
    }
  }, [_vm._v("11")]), _vm._v(" A snapshot, this seems, of the composer caught in the solitude of the moment.")]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "fig0103"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page"
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "images/chi-kramer-fig01003.jpg",
      "width": "511"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 1.3.")]), _vm._v(" Joseph Lange: Portrait in oils of Wolfgang A. Mozart. Before restoration, from a photograph made in 1946. Salzburg, Mozarteum. By kind permission.")])]), _vm._v(" "), _c('p', [_vm._v("Refusing the postures of conventional portraiture, undoing its masks, these images invite us into the private places of the mind. So it is with the music of the Enlightenment. The conventions are well known, and we admire the wit and originality with which the important composers take up their challenges. But then there are those moments when the composer’s ear turns inward, beneath the elegant surface of the music to some less comfortable recess, beyond convention, and very nearly inscrutable, beyond our ability to seize the moment, to grasp its significance. It is this moment—Mendelssohn’s "), _c('i', [_vm._v("Überraschung")]), _vm._v(", Herder’s "), _c('i', [_vm._v("Anerkenntnis")]), _vm._v("—that is endowed, both as a mark in the great temporal expanse of music and as a signifier of refractory meaning. The quest to locate such moments, to grasp their significance, is the itch that animates the paragraphs below and the chapters that follow, where the chromatic inflection opens onto unsuspected terrain, capturing the internal probings of the composer caught between those salient contradictions that the Enlightenment mind struggled to hold in balance, where the music reveals its human face.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0101"
    }
  }, [_c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0006#toc"
    }
  }, [_vm._v("The Chromatic Moment")])]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("In the apparent bedrock of an opposition between diatonic and chromatic inheres a timeless abstraction which each generation of theorists, beginning "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p11"
    }
  }), _vm._v("in ancient Greece (and no doubt earlier and elsewhere as well), interrogates as an aspect of a more broadly conceived idea of how music goes. It is far from my purpose here to attempt anything foundational toward the defining of a theoretical problem, but rather to understand, empirically, how, in the music of the Enlightenment, the tension between these two conditions—the diatonic and the chromatic—plays itself out in a music that seems often enough an exploration of this very tension.")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0102"
    }
  }, [_vm._v("I")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("We begin in 1762, on the final page of Emanuel Bach’s "), _c('i', [_vm._v("Versuch über die wahre Art das Clavier zu spielen")]), _vm._v(". Tipped into the book at just this place is a handsome copper-plate engraving of a Fantasia in D, a modest piece on its surface, far less challenging than the grand Fantasia in C Minor with which Bach brought to a telling conclusion his "), _c('i', [_vm._v("Probestücke")]), _vm._v(", those eighteen study pieces published with Part 1 of the "), _c('i', [_vm._v("Versuch")]), _vm._v(" in 1753. This lesser Fantasia, a "), _c('i', [_vm._v("Probestuck")]), _vm._v(" of another kind, is however significant in quite another sense, for it was offered as a final illustration at the end of a lengthy disquisition titled “Von der freyen Fantasie”—offered, in fact, in two forms: as a "), _c('i', [_vm._v("Gerippe")]), _vm._v(" (literally, a skeleton), a representation of the piece as figured bass, typeset on the last page of text; and, in the engraved plate, as an "), _c('i', [_vm._v("Ausführung")]), _vm._v(" (a performance; a realization, in that paradoxical condition somewhere between improvisation and composition)."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn16",
      "id": "fn16r"
    }
  }, [_vm._v("12")]), _vm._v(" (The two are shown in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#fig0104"
    }
  }, [_vm._v("fig. 1.4")]), _vm._v(".) The complex relationship between the two—between "), _c('i', [_vm._v("Gerippe")]), _vm._v(" and "), _c('i', [_vm._v("Ausführung")]), _vm._v("—has been long admired, ever since Heinrich Schenker took this final chapter as the topic of his own essay toward a theory of improvisation."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn17",
      "id": "fn17r"
    }
  }, [_vm._v("13")])]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "fig0104"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p12"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "599",
      "src": "images/chi-kramer-fig01004.jpg",
      "width": "343"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Figure 1.4.")]), _vm._v(" Carl Philipp Emanuel Bach, "), _c('i', [_vm._v("Versuch über die wahre Art das Clavier zu spielen")]), _vm._v(", Part 2 (Berlin, 1762), 341, and unpaginated plate.")])]), _vm._v(" "), _c('p', [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p13"
    }
  }), _vm._v("My purpose is more modest. I want simply to root around in Bach’s language, savoring his way of talking through the events of the Fantasia, seeking an entry into our larger topic. The text of the full paragraph has been given many times. Here is the core of it:")]), _vm._v(" "), _c('div', {
    staticClass: "blockquote"
  }, [_c('p', {
    staticClass: "bqs"
  }, [_vm._v("At (1) we see the long sustaining of the harmony in the principal key at the beginning and at the end. At (2) a modulation to the fifth is advanced, where we remain for quite a while, until, at (x) the harmony proceeds to E minor. The three notes at (3), under which a slur is drawn, elucidate the preparation for the following repetition of the chord of the second, which is retaken via an inversion of the harmony. The preparation at (3) is realized through slow figures, in which the bass has been intentionally omitted. The transition from the B with the seventh chord to the following B"), _c('span', {
    staticClass: "ccust2"
  }, [_c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")])]), _vm._v(" with the second chord reveals an ellipsis, for a six-four chord on B or a triad on C ought really to have preceded it. At (4) the harmony appears to proceed to D minor, but instead, with the omission of the minor triad on D, the augmented fourth in the second-chord on C is taken, as if one were to modulate to G major, but G minor is taken in its place (6), whereupon, through frequent dissonant chords, the music is returned to the tonic, and the Fantasia closes with an organ point.")])]), _vm._v(" "), _c('p', {
    staticClass: "pcon"
  }, [_vm._v("No talk here of anything resembling thematic substance. In a comparison of the earlier C-minor Fantasia with this one, Bach writes merely that the earlier one is “mit vieler Chromatik vermischet” (interlarded with much chromaticism) while this one consists “"), _c('b', [_vm._v("mehrentheils")]), _vm._v(" [emphasis Bach’s] aus ganz natürlichen und gewöhnlichen Sätzen” ("), _c('span', {
    staticClass: "u"
  }, [_vm._v("for the most part")]), _vm._v(" of quite natural and usual passages). Harmony is the main business—not, however, as a thing in itself, admired for its sonorous beauty or its shaping of phrases, but rather as a function of what might be called the chromatic moment. What matters here is the gradual and deliberate deployment of chromatic tones toward a point of greatest remove from the tonic, the advance of each tone celebrated in some new "), _c('i', [_vm._v("Figur")]), _vm._v(", in turn setting off a music that approaches the condition of theme. The very first chromatic tone, the G"), _c('span', {
    staticClass: "ccust2"
  }, [_c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")])]), _vm._v(" marked by Bach’s numeral "), _c('b', [_vm._v("2")]), _vm._v(", provokes just such an event, as if liberating the music from a locked-down tonic. (The numerals are entered only in the figured-bass example, and, tellingly, not in the full-blown fantasy.) Even at the moment of its resolution, "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p14"
    }
  }), _vm._v("the bass having moved to A, it is the G"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" that lingers, isolated high in the treble and sustained, dissonantly. Only the most austere player would resist the temptation to indulge that high G"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" with "), _c('i', [_vm._v("Bebung")]), _vm._v(", that tremulous vibrating of the string that, among keyboard instruments, only the clavichord can produce. At the end of its duration, the tone is embellished with a "), _c('i', [_vm._v("Doppelschlag")]), _vm._v(", pressing toward a resolution that is frustrated by the introduction of the next chromatic tone: a D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" which at once divests the A of its function as the root of a new tonic. Now a dissonant seventh above B, it drives the music to a farthest reach in its chromatic adventure. Bach’s "), _c('b', [_vm._v("X")]), _vm._v(" only signals what is to happen here—or rather, what "), _c('i', [_vm._v("might")]), _vm._v(" happen—in the "), _c('i', [_vm._v("Ausführung")]), _vm._v(". Bach speaks of a modulation to E minor, and to a subsequent “ellipsis” when the bass moves from B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♮")]), _vm._v(" (a root) to B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" (a seventh below a root C)."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn18",
      "id": "fn18r"
    }
  }, [_vm._v("14")])]), _vm._v(" "), _c('p', [_vm._v("It is precisely here, where the theoretical idea of the piece is at its most extreme, at the continental divide between sharp side and flat side, where the drive outward toward ever more remote fifths from the tonic is corrected, abruptly and, so to say, irrationally, at the turn toward the subdominant—in this case, and in many others, toward the minor subdominant (achieved, finally, at Bach’s numeral "), _c('b', [_vm._v("6")]), _vm._v("): it is here, at this inscrutable moment—this ellipsis, as Bach calls it—that the music springs to life, as though in search of its meaning. Runs and arpeggiations give way to music of substance. Those big dotted chords announce the moment. An eloquent rhetoric is engaged. The deep B is sustained beneath a serpentine elaboration of the seventh chord, now in languorous eighth notes, whose curves intimate thematic shape. And here is where theory and practice are in dispute. The sustained B in the "), _c('i', [_vm._v("Gerippe")]), _vm._v(" can’t account for all those notes that evolve from it in the "), _c('i', [_vm._v("Ausführung")]), _vm._v(". Even Bach’s description of the ellipsis—the theoretical gap between this deep B and the B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" that follows—doesn’t quite explain what happens here."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn19",
      "id": "fn19r"
    }
  }, [_vm._v("15")]), _vm._v(" It is as though Bach pushes the music to its limit, toward a tension at the edge of coherence.")]), _vm._v(" "), _c('p', [_vm._v("Ellipsis is a device that toys with reason, containing within its space a violation of some syntactic rule: reason and its adversary head to head. It is "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p15"
    }
  }), _vm._v("this tension, at the elliptical moment, where imagination (the improvisatory "), _c('i', [_vm._v("Ausführung")]), _vm._v(") and its staid companion (the grounded structure of a figured bass, and its extension in some greater tonal design) go at each other. What is creation, Bach seems to say, if not this drive toward the edge, the control of reason holding hard against the flight of the imagination? This latter condition is eloquently amplified by Johann Georg Sulzer as a music performed “from a certain fullness of feeling and in the fire of inspiration” [aus einer gewissen Fülle der Empfindung und in dem Feuer der Begeisterung]."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn20",
      "id": "fn20r"
    }
  }, [_vm._v("16")]), _vm._v(" Sulzer’s powerful metaphor for the unleashing of the creative act overshadows his failure to recognize the grounding of the act in the no less powerful restraint of what might be called the theoretical imperative.")]), _vm._v(" "), _c('p', [_vm._v("To read this final illustration in the "), _c('i', [_vm._v("Versuch")]), _vm._v(" as a lambent mark in the unfolding of the Enlightenment tapestry would risk exaggerating its significance. On the face of it, its meaning in Bach’s project is simple: to articulate the crossing of a border from rudimentary keyboard pedagogy to the improvisatory process of fantasia—"), _c('i', [_vm._v("fantasieren")]), _vm._v(", a verb, an act—as requisite to mature composition."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn21",
      "id": "fn21r"
    }
  }, [_vm._v("17")]), _vm._v(" We are witness to this rare glimpse into the mind of the composer even as he composes—or, more accurately, as he attempts to explain, "), _c('i', [_vm._v("ex post facto")]), _vm._v(", how the improvisatory rush of the piece is to be reconciled with a timeless and unforgiving theoretical bedrock that underlies the music. But the music has its own story to tell. These chromatic moments are identified not merely in elaboration of some modulatory scheme, as equidistant modules of architectural structure. Rather, they are conceived as "), _c('i', [_vm._v("dramatic")]), _vm._v(" events, endowed with meaning, each celebrated, in some idiosyncratic way, thematically, rhetorically. This is what the piece is "), _c('i', [_vm._v("about")]), _vm._v(".")]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0103"
    }
  }, [_vm._v("II")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("It is in this sense, in its focus on the chromatic moment and the ellipsis that it provokes, that Bach’s final paragraph can be said to open into the larger "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p16"
    }
  }), _vm._v("theater of Enlightenment music. One memorably daring engagement with this idea is to be encountered in the first movement of Mozart’s magisterial Quintet for Strings, K. 515 (dated 19 April 1787, in the thematic catalogue of his works that Mozart had begun to keep in February 1784"), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn22",
      "id": "fn22r"
    }
  }, [_vm._v("18")]), _vm._v("), a moment which we shall explore more fully in the next chapter. Here, I want only to remind us of those very grand arpeggiations in C major with which the work opens, so conspicuously "), _c('i', [_vm._v("diatonic")]), _vm._v(", an aura enhanced by the key itself: there is something about C major, this blankest of keys, that only sets in sharp relief the slightest chromatic infraction—and by “blank” I mean, too obviously, to play upon that aspect of chromaticism that has to do with color, for the etymology of the word is itself bound up in the concept. And so it is in this context that a detonation of the chromatic at measure 15 sets the music off in all those other directions that give meaning to the work: a simultaneity (as we once called these things) whose notes—from bottom to top: F, C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(", G, B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(", E—literally stop the music in its tracks. (See below, "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0012#ex0202"
    }
  }, [_vm._v("ex. 2.2")]), _vm._v(".) We, and the players of those edgy notes, must pause as well, if figuratively, in bemused contemplation of the significance of the moment, a significance that we can only vaguely surmise, for its meaning will come clear in the leisurely expanse of time in which this story unfolds.")]), _vm._v(" "), _c('p', [_vm._v("Two years after the composition of Mozart’s quintet, a nineteen-year-old Beethoven confronted the challenge of the chromatic in a very different way. Among the keyboard music of his Bonn years are two Preludes through all the keys, composed (we are to believe) in 1789. That, at any rate, is the date, possibly in Beethoven’s hand, at the top of a score otherwise in the hand of a copyist, but to which Beethoven put his signature."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn23",
      "id": "fn23r"
    }
  }, [_vm._v("19")]), _vm._v(" Beethoven evidently thought well enough of these curious pieces to allow their publication in 1803. In both of them, Beethoven proceeds methodically, even pedantically, from C major through seven fifths ascending along the sharps axis, till C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(", and it is here, and "), _c('span', {
    staticClass: "u"
  }, [_vm._v("not")]), _vm._v(" at the symmetrical division of the octave at F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v("/G"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(", that the bridge is crossed to the flat side. The crossing is enacted not without "), _c('a', {
    staticClass: "page"
  }), _vm._v("some deeper recognition of the existential moment. In the first of the two preludes, as if to celebrate the achievement of C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" major, the modulatory formula, with its busy eighths and sixteenths, is forsaken at measure 38 for an almost penitential texture and a new profile that adumbrates the theme that will govern the second prelude. The moment is sustained, and at measure 47 the music turns to C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" "), _c('i', [_vm._v("minor")]), _vm._v(", in that lean, spaced-out texture almost prophetic of the late quartets. (See "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#ex0101b"
    }
  }, [_vm._v("ex. 1.1B")]), _vm._v(".)")]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "ex0101a"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p17"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "384",
      "src": "images/chi-kramer-ex01001a.jpg",
      "width": "600"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Ex. 1.1.")]), _vm._v(" Beethoven, Two Preludes through the Twelve Major Keys, op. 39.")]), _vm._v(" "), _c('p', {
    staticClass: "figcap"
  }, [_vm._v("A. No. 1, mm. 1–13.")])]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "ex0101b"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p18"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "601",
      "src": "images/chi-kramer-ex01001b.jpg",
      "width": "510"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Ex. 1.1.")]), _vm._v(" Beethoven, Two Preludes through the Twelve Major Keys, op. 39.")]), _vm._v(" "), _c('p', {
    staticClass: "figcap"
  }, [_vm._v("B. No. 1, mm. 38–60.")])]), _vm._v(" "), _c('p', [_vm._v("“Praeludium durch die 12 Dur-Tonarten” (through the twelve "), _c('i', [_vm._v("major")]), _vm._v(" keys), the manuscript reads, and so this turn to C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" minor seems a furtive escape, a stolen moment from that other mode. And then, apparently to prolong the enharmonic moment, if not to obscure it, the music moves through a true Neapolitan in C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" minor, now leading the harmony chromatically back to C (but now minor), then along the flats axis until D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major. But if we were about to extrapolate some theory of enharmonics from this moment, Beethoven disabuses us, for in the second Prelude, the negotiation (at measure 21) between C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" major and D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major is immediate and direct (see "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#ex0102"
    }
  }, [_vm._v("ex. 1.2")]), _vm._v("). C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" "), _c('i', [_vm._v("becomes")]), _vm._v(" D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(". The keyboard player may feel as though nothing much "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p20"
    }
  }), _vm._v("has happened, but the string player, if this were his music, would struggle to reconcile the discrepancy. The music is now recast in register, its texture reformulated. Briefly in five voices, three of them sustaining pedal tones on D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(", the motive is set deep, in imitative motet style, once more in homage to the moment, and perhaps in unconscious acknowledgment of the medieval durus/mollis."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn24",
      "id": "fn24r"
    }
  }, [_vm._v("20")]), _vm._v(" It is as though the modulatory extravagance that in the first Prelude drove the music into the gap between C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" and D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" is here pointedly refused. In strict theoretical terms, the music must at this turn chart its descent through the twelve fifths that stand between C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" and D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(". Here, in its place, is the extreme ellipsis.")]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "ex0102"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p19"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "600",
      "src": "images/chi-kramer-ex01002.jpg",
      "width": "533"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Ex. 1.2.")]), _vm._v(" Beethoven, Two Preludes, op. 39, no. 2: mm. 1–29.")])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0104"
    }
  }, [_vm._v("III")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("If these two Preludes capture a young Beethoven exploring the enharmonic enigma, the modulatory trajectories of the Fantasia (Adagio) in Joseph Haydn’s String Quartet in E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" Major, op. 76, no. 6 (likely composed in 1797), sets its readers a very different challenge, and one that has been broached in earlier theoretical essays."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn25",
      "id": "fn25r"
    }
  }, [_vm._v("21")]), _vm._v(" Here is a work that begins in B major (without key signature, an orthographic convention in the notation of fantasies of a certain kind) and plots its way through a number of keys, establishing a few as stations for a restatement of its opening theme, before settling for good in B major, anointed with a proper key signature, at measure 60 and through to the end at measure 112. The narrative unfolds in two large cycles: a modulatory"), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p21"
    }
  }), _vm._v(" first cycle, through measure 59, and a more conventionally stable second cycle.")]), _vm._v(" "), _c('p', [_vm._v("It is toward the end of the first of these cycles that the chromatic moment is exercised in an enharmonic locution whose notation hints at deeper hermeneutic readings. (The entire passage is shown in "), _c('a', {
    staticClass: "xref",
    attrs: {
      "href": "#/chikramer0010#ex0103"
    }
  }, [_vm._v("ex. 1.3")]), _vm._v(".) Having returned to B major at the close of a statement of the theme that had begun in B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major, the music continues from the cadence at measures 38–39 in reaffirmation of this sense of return, and then, in a sudden and catastrophic turn of events, breaks down on a diminished-seventh chord at measure 46, its dissonant E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♮")]), _vm._v(" relocated to the lowest octave, where it sounds in isolation. The music pauses here as though deep in thought, pondering the implications of its predicament. Here, it would be good to remind ourselves that for Haydn and his contemporaries, syntax is everything: the diminished seventh will be heard as a dominant ninth, where the ninth is what the Berlin theorist Johann Philipp Kirnberger would call an “inessential” [zufällige] dissonance, displacing the understood (and unsounded) root of the harmony. For Kirnberger, the true seventh of the dominant is an “essential” dissonance that resolves only with a change of root."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn26",
      "id": "fn26r"
    }
  }, [_vm._v("22")]), _vm._v(" What, then, shall we hear as the displaced root of the harmony at measure 46? One plausible candidate is F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(", the F"), _c('span', {
    staticClass: "ccust1"
  }, [_vm._v("𝄪")]), _vm._v(" in the second violin behaving as an implied G"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♮")]), _vm._v(", the flat ninth as appoggiatura to the true root of a dominant on F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(". Our isolated E would then be heard as the essential seventh deep in the bass. And yet, its immediate context would encourage us to hear that harmony no less persuasively as a dominant on D"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(", the E suspended above it delaying the arrival of a root.")]), _vm._v(" "), _c('div', {
    staticClass: "figure",
    attrs: {
      "id": "ex0103"
    }
  }, [_c('p', {
    staticClass: "fig"
  }, [_c('a', {
    staticClass: "page",
    attrs: {
      "id": "p22"
    }
  }), _c('img', {
    attrs: {
      "alt": "",
      "height": "601",
      "src": "images/chi-kramer-ex01003.jpg",
      "width": "534"
    }
  })]), _vm._v(" "), _c('p', {
    staticClass: "figh"
  }, [_c('span', {
    staticClass: "fighn"
  }, [_vm._v("Ex. 1.3.")]), _vm._v(" Haydn, String Quartet in E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" Major, op. 76, no. 6, second movement, mm. 39–63.")])]), _vm._v(" "), _c('p', [_vm._v("The harmonies that follow at measures 47 and 48, each occupying its own measure—fermata-lengthened, as though to suspend the moment in timeless contemplation—only complicate this hearing. If the diminished seventh at measure 46 is to be heard as a dominant on F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(", its magnetic field will draw in the diminished seventh at measure 47, so that the two together will imply a succession of the roots F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" and C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(", its bass suggesting the upward motion E through E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" to a root-position dominant on F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" that does not come. Within this aura, the root-position dominant seventh on E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" that follows seems a non sequitur, effectively reversing the magnetic field so that, in retrospect, "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p23"
    }
  }), _vm._v("we hear the harmony at measure 47 as a dominant on B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(", its upper note a misspelling of C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(". That dissonant E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♮")]), _vm._v(" in the bass at measure 46 will now be re-construed as F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(", an appoggiatura that will resolve to the deep E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" at measure 48. Haydn’s "), _c('i', [_vm._v("con licenza")]), _vm._v(" admits to the enharmonic sleight of hand in the notation, but also to the odd displacement of the B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" in the first violin to an inner voice, evidently to avoid the implication of parallel (or rather, “covered”) fifths in the outer voices from measure 47 to measure 48.")]), _vm._v(" "), _c('p', [_vm._v("In their sphinx-like notation, these three bars challenge us to hear an inflection of meaning in the music that now follows. Squarely in A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major, this new exposition of the principal theme assumes a posture and a significance that no other statement of it can claim: here, indeed, is the last time that the theme will be heard in its full eight measures. Here, too, the outer voices are separated by two full octaves—in every other statement, the distance is an octave—so that the deep A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" feels more firmly grounded. Even the grace-note A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" in the first violin, delivering the theme into an upper register, has its baggage, having been forced into a lower register by the curious disposition of the B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" in measure 48. And so, the opening of the theme here sounds, if only momentarily, in two registers.")]), _vm._v(" "), _c('p', [_vm._v("The apparent obfuscation around these three chords, in the fragile web of their relationship to one another, has then a true bearing on the significance of the piece, for the emergence of the theme in A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major now resonates with a clarity that demystifies these obscure harmonies, setting itself apart from the labyrinthian modulatory track of the first cycle. It is as though the grinding progress of the piece, from its inception in B major up through these elliptical chords, has been in fitful, empirical quest to find its bearings, to locate a center. Indeed, the centrality of A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" seems only underscored in the manner in which it is abandoned. The utterly plain extension of A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" in the four bars that follow the cadence (measures 56–59) comes upon B major inadvertently, an echo of those earlier, similarly unexpected discoveries of E major and B"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major. To put it simply, the return to B major is endowed with none of the significance—dramatic, rhetorical, logical—that one might otherwise claim for the retrieval of the tonic after excursions of this extreme tonal range.")]), _vm._v(" "), _c('p', [_vm._v("Still, the syntax of the movement is not quite so simply parsed. To the Enlightenment mind, this apparent antithesis of A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major and B major would be apprehended less as ratio, in the language of enharmonic abstraction, than as a tension between players staged in a drama of wit and irony. Within the Adagio, Emanuel Bach’s vision of ellipsis seems apropos. For it does not take "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p24"
    }
  }), _vm._v("a bold leap of imagination to understand the return to B major at measure 60 as a delayed response to the music beginning at measure 39 and running through the two diminished sevenths. In one sense, the stopping of time precisely here—before, that is, the intervention of the dominant seventh on E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v("—puts us at the threshold of the return that comes finally at measure 60. The missing harmony is the root-position dominant at F"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" that might have come at measure 48. In its place, we are led to this otherworldly sounding of the theme in a remote A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(".")]), _vm._v(" "), _c('p', [_vm._v("Yet, in the larger theater of the quartet, A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" is anything but otherworldly. This is the key in which the Adagio might well have sounded—"), _c('i', [_vm._v("ought")]), _vm._v(" to have sounded, if we are reading accurately the signs of its statement at measure 49. And if this is so, we are then witness to a reversal of the diatonic/chromatic circuitry. In the quartet-world of E"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" major, B major is the extreme outlier, eight fifths away on the spectrum of ascending fifths toward the sharps side."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn27",
      "id": "fn27r"
    }
  }, [_vm._v("23")]), _vm._v(" The finding of A"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♭")]), _vm._v(" is a journey inward toward the diatonic."), _c('a', {
    staticClass: "fnref",
    attrs: {
      "href": "#/chikramer0024#fn28",
      "id": "fn28r"
    }
  }, [_vm._v("24")])]), _vm._v(" "), _c('h2', {
    staticClass: "ah",
    attrs: {
      "id": "ah0105"
    }
  }, [_vm._v("IV")]), _vm._v(" "), _c('p', {
    staticClass: "paft"
  }, [_vm._v("If, in the Enlightenment and beyond, music is born of an increasingly complex dialectic between two tonal spheres, each of its tonics generating its own array of over- and undertones, then the identity of one of these areas as a chromatic function of the other readily plays out in a confrontation of substance, so that it no longer seems quite adequate to speak of the one as the secondary issue (a chromatic embellishment) of a primary, diatonic basis. And yet, when the game is over, when the temporalities of this confrontation are exhausted and the piece is returned, as by convention it must be, to its tonic of origin, then (paradoxically, perhaps) we are inclined to view such relationships hierarchically, and the question then arises whether this "), _c('a', {
    staticClass: "page",
    attrs: {
      "id": "p25"
    }
  }), _vm._v("hierarchy ought not itself to be understood dialectically. In certain pieces, the anti-tonic, the remote key, is the destination, the locus of music-making at its most inventive while the return to the tonic is conventional, even "), _c('i', [_vm._v("en passant")]), _vm._v(". If this way of construing the tonal spectrum is common currency in early Romantic thought, the idea might be extended back into the Enlightenment—even to this curious Prelude of Beethoven’s from 1789, and its evocation of C"), _c('span', {
    staticClass: "ccust2"
  }, [_vm._v("♯")]), _vm._v(" minor. In such cases, the chromatic might be said to be where the action is, tending thereby to usurp the function of the tonic: the chromatic, that is, tends toward the diatonic, the diatonic toward the chromatic. If the tonal systems of a Haydn or a Beethoven won’t allow this to happen in any complete sense, we might at least work this discomfiting idea into an understanding of their music.")])])])])
}]}


//////////////////
// WEBPACK FOOTER
// ../node_modules/vue-loader/lib/template-compiler?{"id":"data-v-4bde203e","hasScoped":false,"transformToRequire":{"video":"src","source":"src","img":"src","image":"xlink:href"}}!../node_modules/vue-loader/lib/selector.js?type=template&index=0!./src/components/chi-kramer-0010.vue
// module id = 81
// module chunks = 0